/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaMinus, FaShieldAlt } from 'react-icons/fa';
import './privacyStyles.scss';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

// Accordion item component
function AccordionItem({
  title, children, isOpen, onClick,
}) {
  return (
    <motion.div
      className={`accordion-item ${isOpen ? 'open' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="accordion-header" onClick={onClick}>
        <h3>{title}</h3>
        <motion.div
          className="icon-container"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {isOpen ? <FaMinus /> : <FaPlus />}
        </motion.div>
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
        <motion.div
          className="accordion-content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="content-inner">
            {children}
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Privacy() {
  const [openSection, setOpenSection] = useState(0);

  const toggleSection = useCallback((index) => {
    setOpenSection((prev) => (prev === index ? -1 : index));
  }, []);

  const sections = [
    {
      title: '1. Responsable du traitement',
      content: (
        <p>
          <strong>Drivesoft</strong><br />
          SAS au capital de 1000 euros<br />
          Immatriculée au RCS de Pontoise sous le numéro 93982079100019<br />
          Siège social : 3 RUE MARLENE DIETRICH, 95220 HERBLAY-SUR-SEINE<br />
          Représentée par BRX Software & Invest<br />
          Contact : <a href="mailto:contact@drivesoft.fr">contact@drivesoft.fr</a>
        </p>
      )
    },
    {
      title: '2. Finalités du traitement',
      content: (
        <>
          <p>Vos données sont collectées et traitées pour les finalités suivantes :</p>
          <ul>
            <li>Gestion des missions de transport sanitaire : attribution des missions aux équipages, suivi des plannings, gestion des tâches et des processus opérationnels.</li>
            <li>Gestion du personnel : administration des informations des employés (permis de conduire, visites médicales, pointage des heures, etc.).</li>
            <li>Facturation et gestion financière : création et modification des factures, télétransmission à la Sécurité sociale, gestion des règlements et impayés.</li>
            <li>Géolocalisation des véhicules : optimisation des trajets et suivi en temps réel des véhicules via boîtiers OBD.</li>
            <li>Gestion des stocks et du matériel médical : inventaire via QR code, suivi des dates de révision et des documents de conformité.</li>
            <li>Statistiques et tableaux de bord : analyse des performances (chiffre d'affaires, masse salariale, taux de présence, etc.).</li>
            <li>Conformité légale : respect des obligations comptables et sociales (ex. : conservation des factures).</li>
          </ul>
        </>
      )
    },
    {
      title: '3. Catégories de données traitées',
      content: (
        <>
          <p>Drivesoft traite les catégories de données personnelles suivantes :</p>
          <ul>
            <li><strong>Données d'identification :</strong> nom, prénom, adresse, numéro de téléphone, email.</li>
            <li><strong>Données professionnelles :</strong> permis de conduire, visites médicales, diplômes, heures de travail, plannings.</li>
            <li><strong>Données de santé :</strong> informations relatives aux patients transportés (uniquement si nécessaires pour la mission et la facturation).</li>
            <li><strong>Données de géolocalisation :</strong> positions des véhicules via boîtiers OBD.</li>
            <li><strong>Données financières :</strong> informations de facturation, données des assurés (Sécurité sociale, mutuelle), tarifs.</li>
          </ul>
          <p>Les données sensibles, telles que les données de santé, font l'objet de protections renforcées conformément à l'article 9 du RGPD.</p>
        </>
      )
    },
    {
      title: '4. Bases juridiques du traitement',
      content: (
        <>
          <p>Les traitements de données sont fondés sur les bases juridiques suivantes :</p>
          <ul>
            <li><strong>Exécution du contrat :</strong> pour fournir les services aux clients (ex. : gestion des missions, facturation).</li>
            <li><strong>Consentement :</strong> pour certaines données optionnelles, comme la géolocalisation des véhicules lorsque cela est facultatif.</li>
            <li><strong>Obligations légales :</strong> pour respecter les obligations comptables, fiscales et sociales (ex. : conservation des factures pendant 10 ans).</li>
            <li><strong>Intérêt légitime :</strong> pour améliorer nos services et sécuriser les données (ex. : statistiques de performance).</li>
          </ul>
        </>
      )
    },
    {
      title: '5. Destinataires des données',
      content: (
        <>
          <p>Les données personnelles peuvent être partagées avec :</p>
          <ul>
            <li>Le personnel autorisé de Drivesoft : selon les rôles et habilitations définis.</li>
            <li>Sous-traitants : prestataires techniques (ex. : hébergeurs certifiés HDS pour les données de santé), liés par des clauses de confidentialité et de sécurité.</li>
            <li>Autorités compétentes : en cas d'obligation légale (ex. : demande de la CNIL ou d'un tribunal).</li>
          </ul>
        </>
      )
    },
    {
      title: '6. Durée de conservation',
      content: (
        <>
          <p>Les données sont conservées pour les durées suivantes :</p>
          <ul>
            <li><strong>Données du personnel :</strong> 5 ans après la fin du contrat de travail.</li>
            <li><strong>Données de facturation :</strong> 10 ans (obligations comptables).</li>
            <li><strong>Données de géolocalisation :</strong> 1 mois.</li>
            <li><strong>Données des patients :</strong> durée strictement nécessaire à la mission de transport.</li>
          </ul>
          <p>Au terme de ces durées, les données sont supprimées.</p>
        </>
      )
    },
    {
      title: '7. Droits des personnes concernées',
      content: (
        <>
          <p>Conformément au RGPD, vous disposez des droits suivants :</p>
          <ul>
            <li><strong>Droit d'accès :</strong> consulter vos données personnelles.</li>
            <li><strong>Droit de rectification :</strong> corriger des données inexactes.</li>
            <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données.</li>
            <li><strong>Droit à la limitation :</strong> limiter le traitement dans certains cas.</li>
            <li><strong>Droit d'opposition :</strong> vous opposer au traitement pour des motifs légitimes.</li>
            <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré.</li>
          </ul>
          <p>
            Pour exercer ces droits, contactez-nous à <a href="mailto:contact@drivesoft.fr">contact@drivesoft.fr</a> ou via le formulaire disponible sur notre site. Nous répondrons sous 1 mois.
          </p>
        </>
      )
    },
    {
      title: '8. Mesures de sécurité',
      content: (
        <>
          <p>Drivesoft met en place des mesures techniques et organisationnelles pour protéger vos données :</p>
          <ul>
            <li>Chiffrement : AES-256 pour les données au repos et TLS 1.3 pour les données en transit.</li>
            <li>Authentification multifacteur (MFA) pour les accès sensibles.</li>
            <li>Sauvegardes régulières et chiffrées.</li>
            <li>Audits de sécurité trimestriels.</li>
            <li>Formation du personnel aux bonnes pratiques de sécurité.</li>
          </ul>
        </>
      )
    },
    {
      title: '9. Transferts de données hors UE',
      content: (
        <p>
          Aucun transfert de données personnelles n'est effectué en dehors de l'Espace Économique Européen (EEE). Toutes les données sont hébergées et traitées en France.
        </p>
      )
    },
    {
      title: '10. Gestion des Cookies',
      content: (
        <>
          <p>Drivesoft utilise des cookies sur son site web pour :</p>
          <ul>
            <li><strong>Cookies techniques :</strong> assurer le bon fonctionnement du site (ex. : gestion des sessions).</li>
            <li><strong>Cookies analytiques :</strong> analyser l'utilisation du site (avec votre consentement préalable).</li>
          </ul>
          <p>Vous pouvez gérer vos préférences via le bandeau de consentement affiché lors de votre première visite.</p>
        </>
      )
    }
  ];

  return (
    <motion.div
      className="privacy-policy-container"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="privacy-content">
        <motion.div className="header-container" variants={itemVariant}>
          <div className="icon-shield">
            <FaShieldAlt />
          </div>
          <h1>Politique de confidentialité</h1>
          <div className="divider">
            <div className="divider-line" />
          </div>
          <p className="date-update"><strong>Date de dernière mise à jour : 02 mars 2025</strong></p>
        </motion.div>

        <motion.div className="introduction" variants={itemVariant}>
          <p>
            Drivesoft, en tant que responsable du traitement, s'engage à protéger vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés. Cette politique de confidentialité explique comment nous collectons, utilisons, protégeons et traitons vos données personnelles.
          </p>
        </motion.div>

        <motion.div className="accordion-container" variants={itemVariant}>
          {sections.map((section, index) => (
            <AccordionItem
              key={index}
              title={section.title}
              isOpen={openSection === index}
              onClick={() => toggleSection(index)}
              index={index}
            >
              {section.content}
            </AccordionItem>
          ))}
        </motion.div>

        <motion.div className="contact-cta" variants={itemVariant}>
          <h3>Des questions concernant vos données personnelles ?</h3>
          <p>Notre équipe est à votre disposition pour répondre à toutes vos questions concernant la gestion de vos données personnelles.</p>
          <a href="/contact#top" className="contact-button">
            Nous contacter
          </a>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Privacy;
